import React, { ElementType, FC, forwardRef } from 'react';
import {
  Box,
  BoxProps,
  Typography,
  TypographyProps,
  Stack,
  IconButton,
  StackProps,
  IconButtonProps,
  IconProps,
} from '@mui/material';
import { Label } from '@/src/components/label/Label';
import { CloseIcon } from '@/src/components/icon/stroke/CloseIcon';

export interface NotificationProps extends BoxProps<ElementType> {
  /**
   * headline of the notification
   */
  headline?: React.ReactNode;
  /**
   * Custom props for the headline
   */
  headlineProps?: TypographyProps<ElementType>;
  /**
   * Custom props for the title
   */
  titleProps?: TypographyProps<ElementType>;
  /**
   * Custom props for the description typography
   */
  descriptionProps?: TypographyProps<ElementType>;
  /**
   * Custom props for description wrapper
   */
  descriptionWrapperProps?: BoxProps<ElementType>;
  /**
   * Custom props for the wrapper container
   */
  wrapperProps?: BoxProps<ElementType>;
  /**
   * Different color options for the notification
   */
  color?: 'white' | 'grey';
  /**
   * Indicates if the notification is new
   */
  isNew?: boolean;
  /**
   * Custom props for the header container
   */
  headerProps?: BoxProps<ElementType>;
  /**
   * Icon to display in the notification
   */
  icon?: React.ReactNode;
  /**
   * Custom props for the icon container
   */
  iconProps?: BoxProps<ElementType>;
  /**
   * Footer text for the notification
   */
  footer?: React.ReactNode;
  /**
   * Control Buttons to display in the notification
   */
  buttons?: React.ReactNode;
  /**
   * Custom props for the buttons container
   */
  buttonsProps?: StackProps<ElementType>;
  /**
   * Custom props for the footer container
   */
  footerProps?: TypographyProps<ElementType>;
  /**
   * Callback function when the notification is dismissed
   */
  onDismiss?: () => void;
  /**
   * Custom props for the close icon container
   */
  closeIconButtonProps?: IconButtonProps;
  /**
   * Custom props for the close icon
   */
  closeIconProps?: IconProps<ElementType>;
}

/**
 * Component that renders a notification
 */
export const Notification: FC<NotificationProps> = forwardRef(
  (
    {
      sx,
      headline,
      titleProps,
      titleWrapperProps,
      descriptionProps,
      descriptionWrapperProps,
      wrapperProps,
      color,
      isNew,
      headerProps,
      icon,
      iconProps,
      footer,
      buttons,
      buttonsProps,
      footerProps,
      onDismiss,
      closeIconButtonProps,
      closeIconProps,
      children,
      ...rest
    },
    ref,
  ) => {
    const { sx: wrapperSx, ...restWrapper } = wrapperProps ?? {};
    const { sx: headerSx, ...restHeader } = headerProps ?? {};
    const { sx: buttonsSx, ...restButtons } = buttonsProps ?? {};
    const { sx: descriptionSx, ...restDescription } = descriptionProps ?? {};
    const { sx: titleSx, ...restTitle } = titleProps ?? {};
    const { sx: closeIconButtonSx, ...restCloseIconButton } = closeIconButtonProps ?? {};

    const { sx: iconSx, ...restIcon } = iconProps ?? {};
    return (
      <Box
        ref={ref}
        sx={{
          backgroundColor: color === 'grey' ? 'surface.primary' : 'background.primary',
          padding: '16px 16px 24px',
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          position: 'relative',
          ...sx,
        }}
        data-testid="Notification"
        {...rest}
      >
        {icon && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: '50%',
              width: { xs: '32px', md: '56px' },
              height: { xs: '32px', md: '56px' },
              backgroundColor: 'transparent.backdrop',
              flexShrink: '0',
              overflow: 'hidden',
              '& img': { width: '100%', height: '100%', objectFit: 'cover' },
              '& .MuiSvgIcon-root': {
                width: { xs: '22px', md: '32px' },
                height: { xs: '22px', md: '32px' },
              },
              ...iconSx,
            }}
            data-testid="Notification.icon"
            {...restIcon}
          >
            {icon}
          </Box>
        )}
        <Box
          sx={{
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
            display: 'flex',
            gap: '4px',
            flexDirection: 'column',
            // Small padding to prevent focus ring trimming
            padding: '4px',
            // show dismiss button on hover Desktop + Tablet
            '&:hover .notification-dismiss': {
              opacity: '1',
            },
            ...wrapperSx,
          }}
          {...restWrapper}
        >
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1, ...headerSx }}
            {...restHeader}
          >
            {isNew && <Label type="indicator" variant="neutral" />}

            <Typography
              variant="subheadline"
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flexGrow: '1',
                minWidth: '0',
                ...titleSx,
              }}
              data-testid="Notification.headline"
              {...restTitle}
            >
              {headline}
            </Typography>
            {onDismiss && (
              <IconButton
                aria-label="Dismiss notification"
                onClick={onDismiss}
                size="small"
                className="notification-dismiss"
                sx={{
                  padding: '2px',
                  // hide dismiss button Desktop + Tablet, keep on mobile
                  opacity: { xs: '1', md: '0' },
                  // show dismiss button on focus
                  '&.Mui-focusVisible': {
                    opacity: '1',
                  },
                  ...closeIconButtonSx,
                }}
                data-testid="Notification.dismiss"
                {...restCloseIconButton}
              >
                <CloseIcon fontSize="small" className="ballotx" name="close" {...closeIconProps} />
              </IconButton>
            )}
          </Box>
          <Box data-testid="Notification.description" {...descriptionWrapperProps}>
            {typeof children === 'string' ? (
              <Typography
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: '2',
                  WebkitBoxOrient: 'vertical',
                  ...descriptionSx,
                }}
                variant="disclaimer1"
                {...restDescription}
              >
                {children}
              </Typography>
            ) : (
              children
            )}
          </Box>
          {footer && (
            <Typography
              variant="disclaimer1"
              color={'onSurface.os600'}
              data-testid="Notification.footer"
              {...footerProps}
            >
              {footer}
            </Typography>
          )}
          {buttons && (
            <Stack
              direction="row"
              sx={{ mt: 1.5, ...buttonsSx }}
              spacing={1}
              data-testid="Notification.buttons"
              {...restButtons}
            >
              {buttons}
            </Stack>
          )}
        </Box>
      </Box>
    );
  },
);
